import { must } from "@/common/utils/must";
import type { ChangeEvent } from "react";
import {
  CHECKLIST,
  CHECKLIST_BOX,
  CHECKLIST_BOX_INPUT,
  CHECKLIST_BOX_LABEL,
  CHECKLIST_ITEM,
  CHECKLIST_LABEL,
  cx,
  LIST_SELECT_STATUS,
  LIST_SHARE,
  LIST_SHARE_LINK,
  LIST_STATUS,
} from "@/components/shared/checklistClasses";
import {
  FAVORITE_ANNOTATION,
  FAVORITE_CTA,
  FAVORITE_CTA_LABEL,
  FAVORITE_DESCRIPTION,
  FAVORITE_DETAILS,
  FAVORITE_DETAILS_FOOTER,
  FAVORITE_DETAILS_FOOTER_CONTROLS,
  FAVORITE_DETAILS_SCROLLER,
  FAVORITE_FOOTNOTE,
} from "@/components/favorites/favoritesClasses";
import { eventNames } from "@/modules/Constants";
import LinkData, { linkTypes } from "@/plugin/LinkData";
import type { ExperienceArticle, ExperienceId } from "@/types/experience";
import type { ExperienceState } from "@/types/state";

import CartIcon from "@svg/cart.svg";
import { sanitizeHtml } from "@/modules/sanitizeHtml";

const REGION = "Favorites Checklist";

type FavoritesChecklistProps = {
  experienceState: ExperienceState;
  onApplicationEvent: (event: Record<string, unknown>) => void;
  onAddAllArticlesToFavorites?: (region: string) => void;
  onRemoveAllArticlesFromFavorites?: (region: string) => void;
  onAddArticleToFavorites?: (region: string, id: number) => void;
  onRemoveArticleFromFavorites?: (region: string, id: number) => void;
};

export default function FavoritesChecklist({
  experienceState,
  onApplicationEvent,
  onAddAllArticlesToFavorites,
  onRemoveAllArticlesFromFavorites,
  onAddArticleToFavorites,
  onRemoveArticleFromFavorites,
}: FavoritesChecklistProps) {
  const experienceData = experienceState.experienceData;

  const onListArticleClicked = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.nodeName === "INPUT") {
      const input = e.target;
      const id = input.getAttribute("data-id");

      if (id === "all") {
        if (input.checked) {
          if (onAddAllArticlesToFavorites) {
            onAddAllArticlesToFavorites(REGION);
          }
        } else if (onRemoveAllArticlesFromFavorites) {
          onRemoveAllArticlesFromFavorites(REGION);
        }
      } else if (input.checked) {
        if (onAddArticleToFavorites) {
          onAddArticleToFavorites(REGION, parseInt(id ?? ""));
        }
      } else if (onRemoveArticleFromFavorites) {
        onRemoveArticleFromFavorites(REGION, parseInt(id ?? ""));
      }
    }
  };

  const getArticles = () => {
    const timeline = experienceData.getTimeline(undefined);
    const max = timeline ? timeline.length : 0;
    const ret: ExperienceArticle[] = [];
    const ids = new Set<ExperienceId>();

    for (let n = 0; n < max; n++) {
      const article = must(
        experienceData.getArticle(undefined, 0, n),
        "timeline article",
      );
      if (!ids.has(article.id)) {
        ids.add(article.id);
        ret.push(article);
      }
    }

    return ret;
  };

  const getChecklistData = () => {
    const favorites = new Set(experienceState.favoritesData.getFavorites());
    const articles = getArticles();
    const list = [];
    let allChecked = true;
    let numChecked = 0;

    for (let n = 0, max = articles.length; n < max; n++) {
      const article = articles[n];
      const isChecked = favorites.has(article.id);

      allChecked = isChecked && allChecked;

      if (isChecked) {
        numChecked++;
      }

      list.push(
        <li key={`checkbox-${n}`} className={CHECKLIST_ITEM}>
          <div className={cx("checkbox", CHECKLIST_BOX)}>
            <input
              className={CHECKLIST_BOX_INPUT}
              type="checkbox"
              data-id={article.id}
              id={`article-checkbox-${n}`}
              aria-labelledby={`article-checkbox-${n}-label`}
              checked={isChecked}
              onChange={onListArticleClicked}
            />
            <label
              className={CHECKLIST_BOX_LABEL}
              htmlFor={`article-checkbox-${n}`}
              aria-labelledby={`article-checkbox-${n}-label`}
            ></label>
          </div>{" "}
          <label
            className={cx("label", CHECKLIST_LABEL)}
            htmlFor={`article-checkbox-${n}`}
            id={`article-checkbox-${n}-label`}
          >
            {article.title}
          </label>
        </li>,
      );
    }

    if (articles.length > 0) {
      list.unshift(
        <li
          className={cx("checkbox-all highlight-fg-1", CHECKLIST_ITEM)}
          key={"checkbox-all"}
        >
          <div className={cx("checkbox", CHECKLIST_BOX)}>
            <input
              className={CHECKLIST_BOX_INPUT}
              type="checkbox"
              data-id="all"
              id={"article-checkbox-all"}
              aria-labelledby="article-checkbox-all-label"
              checked={allChecked}
              onChange={onListArticleClicked}
            />
            <label
              className={CHECKLIST_BOX_LABEL}
              htmlFor={"article-checkbox-all"}
              aria-labelledby="article-checkbox-all-label"
            ></label>
          </div>{" "}
          <label
            className={cx("label", CHECKLIST_LABEL)}
            htmlFor={"article-checkbox-all"}
            id="article-checkbox-all-label"
          >
            Select All
          </label>
        </li>,
      );
    }

    return {
      list: (
        <ul className={cx("article-checklist checklist", CHECKLIST)}>{list}</ul>
      ),
      count: articles.length,
      checked: numChecked,
    };
  };

  const annotation = (
    <span className={FAVORITE_ANNOTATION}>
      {(experienceData.annotation as string | undefined) || "\u00a0"}
    </span>
  );
  const footnote = experienceData.footnote ? (
    <span className={FAVORITE_FOOTNOTE}>
      {experienceData.footnote as string}
    </span>
  ) : null;

  const title = experienceData.name ? (
    <h1 className="favorite-title header-1 tw:mt-[15px]">
      {experienceData.name as string}
    </h1>
  ) : null;
  const description = experienceData.description ? (
    <div
      className={FAVORITE_DESCRIPTION}
      dangerouslySetInnerHTML={{
        __html: sanitizeHtml(experienceData.description),
      }}
    />
  ) : null;

  const listData = getChecklistData();

  return (
    <div className={FAVORITE_DETAILS}>
      <div className={FAVORITE_DETAILS_SCROLLER}>
        {title}
        {description}
        <div className={cx("list-status", LIST_STATUS)}>
          <div className={LIST_SELECT_STATUS}>
            {listData.checked} of {listData.count} selected
          </div>
          <div className={cx("list-share", LIST_SHARE)}>
            {/* biome-ignore lint/a11y/useValidAnchor: a placeholder link with no handler; a real href or a button changes the DOM */}
            <a href="#" className={cx("highlight-fg-1", LIST_SHARE_LINK)}>
              share
            </a>
          </div>
        </div>
        {listData.list}
      </div>
      <div className={FAVORITE_DETAILS_FOOTER}>
        <div className={FAVORITE_DETAILS_FOOTER_CONTROLS}>
          {annotation}
          <button
            type="button"
            className={FAVORITE_CTA}
            aria-label="Shop favorites"
            onClick={() =>
              onApplicationEvent({
                region: REGION,
                name: eventNames.LINK,
                clusterName: "fav",
                cindex: 0,
                index: experienceState.favoritesData.favoriteIndex,
                link: new LinkData(
                  linkTypes.FAVORITE,
                  undefined,
                  "favorite.cta",
                  true,
                ),
              })
            }
          >
            <span className={FAVORITE_CTA_LABEL}>
              <CartIcon className="icon-cart-d tw:relative tw:bottom-[1px] tw:mr-[10px] tw:align-bottom" />
            </span>
          </button>
          {footnote}
        </div>
      </div>
    </div>
  );
}
